import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { getDefaultAgendaImage } from '../../../../../../store/utils/create-event';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import { LanguagesAbbr, PageModule, Session } from '../../../../../../types/working-model';
import { Button } from '../../../../../general-ui/button/button';
import { EIcon } from '../../../../../general-ui/icon/icon';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../../../../live-event/utils';
import SessionCard from './agenda-session-card';
import { filterSessions, SessionFilter, SessionFilterArgs } from './session-filter';

interface LiveSessionsProps {
	module: PageModule;
	template: string;
	sessions: Session[];
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
	isLive: boolean;
	isUpNext?: boolean;
}
export const LiveSessions = ({
	module,
	template,
	sessions,
	language,
	isLive,
	isUpNext,
	baseLanguage
}: LiveSessionsProps): JSX.Element => {
	const [filter, setFilter] = useState<SessionFilterArgs>();
	const [showMore, setShowMore] = useState(false);

	const maxFeaturedSessions = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings.max_featured_sessions);

	const stylingOverrides = getStylingOverrides(module.styling_overrides);
	const { t } = useTranslate('homepage');

	const filteredSessions = useMemo<Session[]>(
		() => filterSessions({ sessions, filter }),
		[sessions, filter]
	);
	const sessionList = maxFeaturedSessions && !showMore
		? filteredSessions.slice(0, maxFeaturedSessions)
		: filteredSessions;

	return (
		<div
			className={classNames(
				'agenda-broadcast-sessions-container agenda-sessions-wrapper'
			)}
		>
			{
				!isUpNext &&
				<div
					className={classNames(
						'agenda-broadcast-sessions-header agenda-sessions-header'
					)}
				>
					{!isLive &&
						<TypographyItem
							className={classNames('agenda-sessions-header-title evt-typography-h3')}
							tagName="h3"
						>
							{t('Live sessions')}
						</TypographyItem>
					}

					<SessionFilter
						isLive={isLive}
						module={module}
						sessions={sessions}
						template={template}
						onFilterChange={setFilter}
						overrides={stylingOverrides.dropdownOverrides}
					/>
				</div>
			}
			<div
				className={classNames('agenda-broadcast-sessions agenda-sessions-list')}
			>
				{sessionList.map((session: Session, i: number) => {
					return (
						<React.Fragment key={session.uuid + i}>
							<SessionCard
								baseLanguage={baseLanguage}
								language={language}
								key={session.uuid}
								session={session}
								stylingOverrides={stylingOverrides}
								template={template}
								defaultImage={getDefaultAgendaImage(template, i)}
							/>
						</React.Fragment>
					);
				})}
			</div>
			{!!maxFeaturedSessions && filteredSessions.length > maxFeaturedSessions && !isUpNext && (
				<div className="agenda-show-more-wrapper">
					<Button
						onClick={() => setShowMore(!showMore)}
						classButton={classNames(
							'secondary',
							stylingOverrides.secondaryButtonOverrides,
							"show-more-btn"
						)}
						template={template}
					>
						{template === TemplateClassNames.Apollo && (
							<EIcon
								name="refresh-transparent"
								position="order-left"
								size="medium"
							/>
						)}
						{t(showMore ? "Less" : "Show more")}
					</Button>
				</div>
			)}
		</div>
	);
};
